import { Component, inject, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { Format, UtilService } from '../../../../../services/util.service';
import { MyCompetitionPerformanceWidgetResponse } from '../../../../../components/my-competition-screen/types';

@Component({
  selector: 'app-spline-chart',
  standalone: true,
  imports: [HighchartsChartModule],
  templateUrl: './spline-chart.component.html',
  styleUrl: './spline-chart.component.scss',
})
export class SplineChartComponent implements OnChanges {
  @Input() brandColors: Record<string, string> = {};
  @Input() format: Format = 'fractionPercentage';
  @Input() data!: MyCompetitionPerformanceWidgetResponse;

  utilService = inject(UtilService);
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartInstance: Highcharts.Chart | undefined;

  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chartInstance = chart;
  };

  ngOnChanges(): void {
    if (!this.data?.myBrand) return;

    const categories = this.data?.myBrand?.dailyPerformance.map(dp => dp.date);
    const competitorSeries: Highcharts.SeriesSplineOptions[] =
    (this.data?.myCompetitors || []).map((competitor) => ({
        name: competitor.brandName,
        data: competitor.dailyPerformance.map(dp => dp.value) as (number | null)[],
        type: 'spline',
        marker: { symbol: 'circle' },
        dashStyle: 'Solid',
        color: this.brandColors[competitor.brandKey] || undefined
      }));
    const series: Highcharts.SeriesSplineOptions[] = [
      {
        name: this.data?.myBrand?.brandName,
        data: this.data?.myBrand?.dailyPerformance.map(dp => dp.value) as (number | null)[],
        type: 'spline',
        marker: { symbol: 'circle' },
        dashStyle: 'ShortDash',
        color: this.brandColors[this.data.myBrand.brandKey]
      },
      ...competitorSeries
    ];

    while (this.chartInstance?.series?.length || 0 > 0) {
      this.chartInstance?.series[0].remove(false);
    }
    series.forEach(s => this.chartInstance?.addSeries(s, false));

    const step = this.calculateStep(categories);

    this.chartOptions = {
      chart: {
        type: 'spline',
        spacingBottom: -5,
        animation: false,
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: categories as string[],
        lineColor: 'transparent',
        labels: {
          style: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '18px',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#686D75',
            lineClamp: 1,
          },
          autoRotation: [],
          autoRotationLimit: 0,
          step: 1,
          rotation: -90,
          formatter: function () {
            const index = this.pos;
            return index % step === 0 ? String(this.value) : '';
          }
        }
      },
      yAxis: {
        title: { text: "" },
        labels: {
          style: {
            color: '#686D75',
            fontSize: '12px',
            fontFamily: 'Poppins',
            fontWeight: '400',
            lineHeight: '18px',
            fontStyle: 'normal',
          },
          formatter: (function (utilService, format) {
            return function () {
              // Handle special case for shortCurrencyNumber format when value is less than 1000
              if (format === 'shortCurrencyNumber' && (this.value as number) < 1_000) {
                return '0';
              }
              return utilService.formatWidgetValue(this.value as number, format) as string;
            };
          })(this.utilService, this.format),
        }
      },
      plotOptions: {
        series: {
          animation: false,
          marker: {
            enabled: false,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                width: 12,
                height: 12,
              }
            }
          },
          showInLegend: true
        }
      },
      legend: {
        useHTML: true,
        symbolWidth: 0,
        align: 'left',
        alignColumns: true,
        itemDistance: 15,
        events: {
          itemClick: () => false,
        },
        itemHoverStyle: {
          cursor: 'default'
        },
        itemStyle: {
          color: '#686D75',
          fontSize: '12px',
          fontFamily: 'Poppins',
          fontWeight: '400',
          lineHeight: '18px',
          fontStyle: 'normal',
        },
        labelFormatter: function () {
          const color = this.color;
          return `
            <span style="display: inline-block;
                        width: 10px; height: 10px;
                        background-color: ${color};
                        border-radius: 50%;
                        margin-right: 3px;">
            </span> ${this.name}
          `;
        }
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        shadow: false,
        backgroundColor: 'transparent',
        padding: 0,
        formatter: (function (utilService, format) {
          return function () {
            const chart = this.points?.[0]?.series?.chart;
            const allSeries = chart?.series || [];
            const visibleSeries = allSeries.filter(s => s.visible);

            let itemsHtml = '';
            visibleSeries.forEach((series, index) => {
              const point = series.data.find(p => p.x === this.x);
              const value = point?.y
                ? utilService.formatWidgetValue(point.y as number, format)
                : utilService.formatWidgetValue(0, format);
              const color = series.color;

              itemsHtml += `
                <div style="display:flex; justify-content:space-between; align-items:center; padding:4px 0; gap:6px;">
                  <div style="display:flex; align-items:center; gap:4px;">
                    <div style="width:8px; height:8px; border-radius:50%; background-color:${color}; flex-shrink:0;"></div>
                    <span style="font-size:12px; font-weight:400; color:#34363A;">${series.name}</span>
                  </div>
                  <div style="font-size:12px; font-weight:600; color:#4E5258;">${value}</div>
                </div>
              `;

              if (index === 0 && visibleSeries.length > 1) {
                itemsHtml += `<hr style="border:none; border-top:1px dashed #DEE0E4; width:100%; margin:0;" />`;
              }
            });

            return `
              <div style="padding:12px; box-shadow:0px 0px 8px 2px rgba(0,0,0,0.15); border-radius:4px; background:#FFFFFF; font-family:'Poppins',sans-serif;">
                <div style="display:flex; flex-direction:column; gap:8px;">
                  <b style="font-size:12px; font-weight:600; color:#34363A;">${this.category}</b>
                  ${itemsHtml}
                </div>
              </div>
            `;
          }
        })(this.utilService, this.format),
      },
      series: series
    }
  }

  private calculateStep(categories: (string | undefined)[]): number {
    const total = categories?.length || 0;
    if (total <= 6) return 1;

    const maxLabels = 8;
    const step = Math.ceil(total / maxLabels);

    return step;
  }
}
